import { useEffect, useState } from 'react';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';

import ColorSchemeButton from '@/components/ColorSchemeButton';
import Icon from '@/components/Icon';
import eventService from '@/services/event';

import Button from '@/components/Button';
import TextField from '@/components/TextField';
import type { Data } from './+data.server';

const Wordmark = ({ className = '' }: { className?: string }) => (
  <span className={`font-display font-bold tracking-tight ${className}`}>
    <span className="text-base-content">Open</span>
    <span className="text-primary">Kai</span>
  </span>
);

const ToriiIcon = ({ size = 40 }: { size?: number }) => {
  const [themeMode, setThemeMode] = useState('dark');

  useEffect(() => {
    const handleThemeUpdate = (updatedTheme: any) => {
      setThemeMode(updatedTheme.themeMode);
    };

    eventService.on('theme:updated', handleThemeUpdate);
    eventService.emit('theme:request-update-announce');

    return () => {
      eventService.off('theme:updated', handleThemeUpdate);
    };
  }, []);

  const src = themeMode === 'dark' ? '/images/icon-dark.svg' : '/images/icon-light.svg';

  return (
    <img
      src={src}
      alt="OpenKai"
      width={size}
      height={size}
      className="inline-block"
    />
  );
};

const EmailCta = ({
  ctaButtonText,
  ctaTag,
  subCtaText,
}: {
  ctaButtonText: string;
  ctaTag: string;
  subCtaText?: string;
}) => (
  <div className="flex flex-col items-center gap-3">
    <div className="flex w-full max-w-md gap-0" data-cta-tag={ctaTag}>
      <TextField
        type="email"
        placeholder="you@company.com"
        aria-label="Email address"
        className="flex-1 [&_.input]:rounded-r-none [&_.input]:bg-base-200 [&_.input]:border-base-300 [&_.input]:placeholder:text-accent [&_.input]:focus:border-primary mb-0"
      />
      <Button
        className="btn-primary rounded-l-none font-display font-semibold tracking-wide uppercase text-xs px-6 h-auto"
      >
        {ctaButtonText}
      </Button>
    </div>
    {subCtaText && (
      <p className="text-xs text-accent max-w-sm text-center leading-relaxed">
        {subCtaText}
      </p>
    )}
  </div>
);

const FloatingNav = () => {
  const { locale, urlPathname } = usePageContext();
  const targetLocale = locale === 'en-US' ? 'es-ES' : 'en-US';
  const label = locale === 'en-US' ? 'EN' : 'ES';
  const href = `/${targetLocale}${urlPathname}`;

  return (
    <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
      <ColorSchemeButton
        className="flex items-center justify-center w-8 h-8 rounded-full bg-base-200/80 backdrop-blur-sm border border-base-300 text-accent hover:text-base-content transition-colors !p-0 !min-h-0"
      >
        {(themeMode: string) => (
          <Icon name={themeMode === 'dark' ? 'moon' : 'sun'} className="w-4 h-4" />
        )}
      </ColorSchemeButton>
      <a
        href={href}
        data-locale-switch
        onClick={(e) => {
          e.preventDefault();
          window.location.href = href;
        }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-base-200/80 backdrop-blur-sm border border-base-300 text-accent hover:text-base-content transition-colors text-xs font-mono uppercase tracking-wider"
      >
        <Icon name="global" className="w-4 h-4" />
        {label}
      </a>
    </div>
  );
};

const ScrollHint = () => {
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;

    const handleScroll = () => {
      const scrollY = root.scrollTop;
      const threshold = window.innerHeight * 0.5;
      setOpacity(Math.max(0, 1 - scrollY / threshold));
    };

    handleScroll();
    root.addEventListener('scroll', handleScroll, { passive: true });
    return () => root.removeEventListener('scroll', handleScroll);
  }, []);

  if (opacity <= 0) return null;

  return (
    <>
      <div
        className="fixed bottom-6 left-6 z-40 text-accent pointer-events-none"
        style={{ opacity, transition: 'opacity 0.15s ease-out' }}
      >
        <Icon name="arrowDown" className="w-6 h-6" />
      </div>
      <div
        className="fixed bottom-6 right-6 z-40 text-accent pointer-events-none"
        style={{ opacity, transition: 'opacity 0.15s ease-out' }}
      >
        <Icon name="arrowDown" className="w-6 h-6" />
      </div>
    </>
  );
};

const Page = () => {
  const { tagline, hero, theGap, whatTheListGets, ctaTag } = useData<Data>();

  const headlineLines = (hero.headline || '').split('\n');
  const gapParagraphs = (theGap.body || '').split('\n\n');

  return (
    <div className="bg-base-100 text-base-content min-h-screen">
      <div id="noScrollFix" hidden />
      <FloatingNav />
      <ScrollHint />
      {/* ── Section 1 — Hero ── */}
      <section className="relative flex flex-col items-center justify-center min-h-screen px-6 py-20 bg-base-100">
        {/* Brand mark */}
        <div className="flex flex-col items-center mb-12">
          <ToriiIcon size={64} />
          <Wordmark className="text-6xl" />
        </div>

        {/* Pre-label */}
        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-accent mb-8">
          {hero.preLabel}
        </p>

        {/* Headline */}
        <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold text-center leading-tight mb-12 max-w-4xl">
          {headlineLines.map((line: string, i: number) => (
            <span key={i} className="block">
              {line}
            </span>
          ))}
        </h1>

        {/* CTA */}
        <EmailCta
          ctaButtonText={hero.ctaButtonText}
          ctaTag={ctaTag.tag}
          subCtaText={hero.subCtaText}
        />

        {/* Tagline at bottom */}
        <p className="absolute bottom-8 font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-accent">
          {tagline}
        </p>
      </section>

      {/* ── Section 2 — The Gap ── */}
      <section className="px-6 py-24 sm:py-32 bg-base-200">
        <div className="max-w-2xl mx-auto space-y-6">
          {gapParagraphs.map((paragraph: string, i: number) => (
            <p
              key={i}
              className={
                i === 0
                  ? 'text-lg sm:text-xl font-display font-semibold text-base-content leading-relaxed'
                  : 'text-base text-accent leading-relaxed'
              }
            >
              {paragraph}
            </p>
          ))}
        </div>
      </section>

      {/* ── Section 3 — What the List Gets ── */}
      <section className="px-6 py-24 sm:py-32 bg-base-100">
        <div className="max-w-2xl mx-auto">
          <p className="text-lg sm:text-xl font-display font-semibold text-base-content mb-10 leading-relaxed">
            {whatTheListGets.intro}
          </p>

          <ul className="space-y-4 mb-10">
            {(whatTheListGets.items || []).map((item: { text: string }, i: number) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-primary font-bold text-lg leading-snug select-none" aria-hidden="true">
                  &mdash;
                </span>
                <span className="text-base text-base-content leading-relaxed">
                  {item.text}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-sm italic text-accent">
            {whatTheListGets.outro}
          </p>
        </div>
      </section>

      {/* ── Section 4 — Bottom CTA ── */}
      <section className="px-6 py-24 sm:py-32 bg-base-200">
        <div className="max-w-xl mx-auto flex flex-col items-center text-center gap-8">
          <ToriiIcon size={48} />
          <h2 className="font-display text-2xl sm:text-3xl font-bold">
            Get on the list.
          </h2>
          <EmailCta
            ctaButtonText={hero.ctaButtonText}
            ctaTag={ctaTag.tag}
          />
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="px-6 py-12 bg-base-100 border-t border-base-300">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-4 text-center">
          <div className="flex items-center gap-2">
            <ToriiIcon size={24} />
            <Wordmark className="text-base" />
          </div>
          <p className="text-xs text-accent">
            Built by DiLescure
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
            {tagline}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Page;
